import { omit } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRelayChat } from '@inference.engine/agentic-react';
import {
  FileAsset,
  type ExchangeProjectContextDto,
  type ProjectContextDto,
  type ProjectContextItemDto,
} from '@inference.engine/api-client';
import { getAgentMetadataHelpers } from '@/entities/agent';
import {
  convertAISdkMessagesToChatMessages,
  type ChatUIMessage,
  type StreamMessageEvents,
  type WidgetActionMessage,
} from '@/entities/ai';
import { createReasonerTransport } from '@/lib/api/reasoner-transport';
import { queryClient } from '@/lib/config';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { CHAT_HISTORY_QUERY_KEY_ROOT } from '@/shared/constants/query-keys.constants';
import { useTranslation } from 'react-i18next';
import { useAiChatAbortHandler } from '../useAiChatAbortHandler';
import { AgentUnavailableError } from './agentUnavailableError';
import type {
  ProcessedChatHistoryLike,
  SendMessageInputLike,
  UseAiChatDeps,
} from './useAiChat.types';
import { measureChatLayout } from '@/shared/lib/chat-layout';

/**
 * Narrows the loosely-typed project-context selection carried on the send
 * input into the wire DTO. The selection holds chip-display fields (`items`)
 * the wire shape doesn't want, so only the project id, the briefs (with their
 * content inline), and the artifact ids survive. Returns undefined when there's
 * no usable selection.
 */
// The composer stores each attached item as `{ id, name, kind }`; the wire and
// the persisted snapshot use `{ id, title, kind, artifactId? }`. A library
// artifact's stored id is its own item id; briefs have no artifact and render
// from the inline snapshot instead.
interface ComposerProjectContextItem {
  id: string;
  name: string;
  kind: string;
}

function toContextItemDto(item: ComposerProjectContextItem): ProjectContextItemDto {
  return {
    id: item.id,
    title: item.name,
    kind: item.kind,
    artifactId: item.kind === 'brief' ? undefined : item.id,
  };
}

function extractProjectContext(selection: unknown): ProjectContextDto | undefined {
  if (!selection || typeof selection !== 'object') return undefined;
  const { projectId, briefs, artifactIds, items } = selection as Partial<ProjectContextDto> & {
    items?: ComposerProjectContextItem[];
  };
  if (typeof projectId !== 'string' || !projectId) return undefined;
  return {
    projectId,
    briefs: Array.isArray(briefs) ? briefs : undefined,
    artifactIds: Array.isArray(artifactIds) ? artifactIds : undefined,
    items: Array.isArray(items) ? items.map(toContextItemDto) : undefined,
  };
}

// Freezes the attached context onto the just-sent message so its chips and
// previews render immediately, matching the snapshot the server persists for
// reloaded history (see EngineService.buildProjectContextSnapshot).
function toProjectContextSnapshot(
  context: ProjectContextDto | undefined,
): ExchangeProjectContextDto | undefined {
  if (!context?.items?.length) return undefined;
  return {
    projectId: context.projectId,
    items: context.items,
    briefs: context.briefs,
  };
}

/**
 * AI chat orchestration hook.
 *
 * All cross-feature deps (`chats`) and cross-app deps (`@/app/store/widget`,
 * `@/app/store/ui-context`, `@/app/store/ui`) are INJECTED via the `deps`
 * bag (dependency injection pattern — see audiences/dashboard/agents canonicalization).
 * The consuming page (`pages/StreamChatSession`) wires the actual
 * stores/hooks/utils and passes them in. This keeps `features/ai`
 * cross-feature-OUT and cross-app-OUT clean.
 */
const widgetActionMarker = ({ artifactId, widgetId, name, title }: WidgetActionMessage) => ({
  artifactId,
  widgetId,
  name,
  ...(title ? { title } : {}),
});

export const useAiChat = (contextId: string, workspaceId: string, deps: UseAiChatDeps) => {
  const { t } = useTranslation('common');
  const messageInFlight = useRef<SendMessageInputLike | null>(null);
  // A resumed turn carries no message of its own: the exchange it continues, approval card included,
  // was already shown, so neither a stop nor a refusal may take it back or touch the draft.
  const isResumingTurn = useRef(false);
  const hasSentMessageRef = useRef(false);
  // Tracks which "MCP tools dropped" notices we've already shown, so a multi-step turn or a handoff
  // chain — each agent run rebuilds its tool set and re-emits — doesn't stack duplicate toasts.
  // Keyed by chat + the dropped set, so a genuinely different drop still surfaces once.
  const notifiedMcpToolDropsRef = useRef<Set<string>>(new Set());

  // Tracks the last chat (route `contextId`) whose history we hydrated, so a
  // refetch for the SAME chat is not treated as a first load (which would
  // re-restore the server agent and clobber a deliberate in-session switch).
  const restoredContextRef = useRef<string | null>(null);

  // The history cache is a snapshot of the exchanges taken when the session
  // mounted (`staleTime: Infinity`, `refetchOnMount: false`), and messages
  // streamed afterwards never reach it. Once we send a message the snapshot
  // is permanently behind the server, so drop it when leaving the session —
  // the next open refetches the full conversation. `removeQueries` (not
  // `invalidateQueries`) because `refetchOnMount: false` suppresses the
  // mount refetch of an invalidated-but-present entry.
  useEffect(() => {
    hasSentMessageRef.current = false;
    return () => {
      if (hasSentMessageRef.current) {
        queryClient.removeQueries({ queryKey: [CHAT_HISTORY_QUERY_KEY_ROOT, contextId] });
      }
    };
  }, [contextId]);

  const {
    currentAgentId,
    setMessages,
    setStreamEvent,
    clearStreamEvents,
    setAgentById,
    setInputOptions,
    setInputFiles,
    setIsConnected,
    setChatId,
    setAiStatus,
  } = deps.chats.useChatStoreHook();

  const {
    clearActiveVersion,
    clearWidgetPanel,
    processWidget,
    reset: resetWidgetState,
    storeWidgetVersion,
  } = deps.app.useWidgetStoreHook();

  const handleAbort = useAiChatAbortHandler({
    messageInFlight,
    deps: deps.abortHandler,
  });

  const prepareChatHistory = (data: ProcessedChatHistoryLike) => {
    // Restore the chat's last agent on (re)load, but don't clobber a deliberate
    // switch: only the genuine FIRST hydration of a given route `contextId` may
    // restore the server-side agent. Keyed off `restoredContextRef` (not
    // `store.chatId`, which is still null here on an empty conversation).
    const store = deps.chats.getChatStoreState();
    const isFirstLoadForContext = restoredContextRef.current !== contextId;
    restoredContextRef.current = contextId;
    // A chat opened with history is resumed, not new: it starts from its own widgets, not
    // the agent's new-session widget nor whatever the previous chat left open. A widget-only
    // agent is the exception: its widget is all it shows, and history never brings it back.
    const isWidgetOnlyAgent = getAgentMetadataHelpers().getIsWidgetOnly(data.lastAgentId);
    const startsFromHistory = data.messages.length > 0 && !isWidgetOnlyAgent;

    // Restore only on the first load AND only when the server agent actually
    // differs — otherwise the redundant `setAgentById` fires an async
    // `loadAgent` that can land after (and overwrite) the user's switch.
    const restoresAgent = isFirstLoadForContext && store.currentAgentId !== data.lastAgentId;
    if (restoresAgent) {
      void setAgentById(data.lastAgentId, false, { withInitialWidget: !startsFromHistory });
    }
    setChatId(contextId);
    setIsConnected(true);

    // A refetch/remount after a deliberate switch would re-store the old
    // agent's widgets from history and snap the panel back. Keep the new state.
    if (!isFirstLoadForContext) {
      return;
    }

    const currentWidget: unknown = deps.app.getWidgetStoreState().widget;

    // A new chat keeps a widget it was opened with (prefilled from the new-session screen);
    // a resumed one drops the previous chat's. So does a widget-only agent being restored: its
    // widget arrives once `setAgentById` loads the agent, and until then the previous chat's
    // must not show under this one. With the agent unchanged nothing would refill the panel,
    // so it is left as it is.
    const dropsPreviousWidget = startsFromHistory || (isWidgetOnlyAgent && restoresAgent);
    if (dropsPreviousWidget) {
      clearWidgetPanel();
      resetWidgetState();
    } else if (!currentWidget) {
      resetWidgetState();
    }

    data.widgets.forEach((widget) => {
      storeWidgetVersion(widget.messageId, widget.agentId, widget.widget, widget.agentTitle);
      // Avoids having to double click the chat widget toggle to open it.
      clearActiveVersion(widget.agentId);
    });
  };

  const syncStoreMessages = (messages: ChatUIMessage[]) => {
    setMessages(
      () => chatHistory?.messages.concat(convertAISdkMessagesToChatMessages(messages)) ?? [],
    );
  };

  const { data: chatHistory, isLoading } = deps.chats.useChatHistoryHook({
    contextId,
    defaultAgentId: currentAgentId,
    enabled: true,
    onSuccess: (data) => {
      // This is to maintain backwards compatibility, we should merge stuff with the store context when we can
      prepareChatHistory(data);

      // Fetch suggestions if there are existing messages in the chat
      if (data.messages.length > 0) {
        const shouldAutoFetch = getAgentMetadataHelpers().getAgentAutoFetchSuggestions(
          data.lastAgentId,
        );
        if (shouldAutoFetch) {
          void deps.chats.getChatStoreState().fetchSuggestions();
        }
      }
    },
  });

  const streamingMessageId = useRef<string | null>(null);
  const runOptionsRef = useRef(deps.runOptions);
  runOptionsRef.current = deps.runOptions;
  const transport = useMemo(
    () =>
      createReasonerTransport({
        contextId,
        workspaceId,
        body: () => runOptionsRef.current?.(contextId) ?? {},
      }),
    [contextId, workspaceId],
  );

  // A handoff reaches the UI only once its target shows something the user can see (its first
  // text, or its run ending), or once a later handoff replaces it. An agent that only routes the
  // turn onward is never shown: applying every handoff as it arrived flicked the header
  // persona → coordinator → persona on a persona switch.
  const pendingHandoffRef = useRef<{
    to: string;
    messageId: string | null;
    textLength: number;
  } | null>(null);
  const visibleTextLengthRef = useRef(0);
  const flushPendingHandoff = useCallback(() => {
    const pending = pendingHandoffRef.current;
    if (!pending) return;
    pendingHandoffRef.current = null;
    clearStreamEvents(); // Clear events from previous agent
    void setAgentById(pending.to, true);
  }, [clearStreamEvents, setAgentById]);

  const {
    messages,
    sendMessage: aiSendMessage,
    status,
    stop,
    setMessages: setAISdkMessages,
  } = useRelayChat<ChatUIMessage>({
    experimental_throttle: 100,
    transport,
    ...(deps.extensions ? { extensions: deps.extensions } : {}),
    ...(deps.runStore ? { store: deps.runStore } : {}),
    onFinish: (data) => {
      flushPendingHandoff();
      if (data.isAbort) {
        if (!isResumingTurn.current) handleAbort(setAISdkMessages);
      } else {
        const cs = deps.chats.getChatStoreState();
        if (cs.chatId && cs.currentAgentId) {
          deps.track('agent_message_received', {
            agent_id: cs.currentAgentId,
            session_id: cs.chatId,
          });
        }

        // Fetch suggestions after stream completes if chat has messages
        const agentId = deps.chats.getChatStoreState().currentAgentId;
        const shouldAutoFetch = getAgentMetadataHelpers().getAgentAutoFetchSuggestions(agentId);
        if (shouldAutoFetch) {
          void deps.chats.getChatStoreState().fetchSuggestions();
        }

        // This keeps the store state in sync with the AI SDK messages, but should not be used for rendering the thread.
        // Only kept in sync as part of the onFinish
        syncStoreMessages(data.messages);
      }

      clearStreamEvents();
      messageInFlight.current = null;
      isResumingTurn.current = false;
    },
    onData: (data) => {
      const event = data as unknown as StreamMessageEvents;

      // Handle tool events
      // TODO: These come through as data parts in the message stream, so we should handle it there instead in the future - this is for backwards compatibility
      if (
        event.type === 'data-toolStart' ||
        event.type === 'data-toolComplete' ||
        event.type === 'data-toolError'
      ) {
        setStreamEvent(event.data.callId, event);
        if (event.type === 'data-toolStart') {
          const cs = deps.chats.getChatStoreState();
          if (cs.chatId && cs.currentAgentId) {
            deps.track('agent_tool_invoked', {
              agent_id: cs.currentAgentId,
              session_id: cs.chatId,
              tool_name: event.data.toolName,
            });
          }
        }
      }

      // The person agreed to the agent's image model, so the composer's chip shows it from now on.
      if (event.type === 'data-image-pick') {
        const { settings, variations } = event.data;
        setInputOptions((prev: Record<string, unknown> | undefined) => ({
          ...(prev ?? {}),
          settings,
          variations,
        }));
      }

      if (event.type === 'data-widget' && streamingMessageId.current) {
        const currentAgentId = deps.chats.getChatStoreState().currentAgentId;
        const producerAgentId = event.data.agentId;
        const metadata = getAgentMetadataHelpers();
        // An unknown producer (an internal sub-agent id) keeps today's root-agent view rather than
        // resolving to none.
        const sourceAgentId =
          producerAgentId !== currentAgentId && metadata.resolveAgentMetadata(producerAgentId)
            ? producerAgentId
            : undefined;
        const widget = {
          ...event.data.widget,
          agentName: producerAgentId,
          ...(sourceAgentId ? { sourceAgentId } : {}),
        };
        const messageId = streamingMessageId.current;

        // Process widget (handles side panel opening and _pendingWidgets)
        processWidget(messageId, widget);

        // Store widget version directly (required for AgentResponse to detect widgets)
        // Use currentAgentId instead of the event.data.agentId. With streaming, we are correctly using the sub-agent's IDs for widgets when it's in a nested flow, but FE is not compatible with how versions are managed for widgets unless the AgentID is pointing to the root caller.
        // TODO: Need to refactor this later - should completely use data-parts in the render component if possible
        const titledBy = sourceAgentId ?? currentAgentId;
        // An agent built in Studio has no title here, only its id, so its widget goes by its own.
        const widgetTitle = (widget.data as { title?: unknown } | undefined)?.title;
        const versionTitle =
          !metadata.resolveAgentMetadata(titledBy) && typeof widgetTitle === 'string' && widgetTitle
            ? widgetTitle
            : t('ai.useAiChat.agentTitleWidget', { agentTitle: metadata.getAgentTitle(titledBy) });
        storeWidgetVersion(messageId, currentAgentId, widget, versionTitle);
      }

      // Only the protocol part switches agents. Acting on the engine's edge marker as well would
      // clear stream events and count the handoff twice.
      if (event.type === 'data-handoff' && 'to' in event.data) {
        const cs = deps.chats.getChatStoreState();
        if (cs.chatId) {
          deps.track('agent_handoff', {
            session_id: cs.chatId,
            from_agent_id: event.data.from,
            to_agent_id: event.data.to,
          });
        }
        pendingHandoffRef.current = {
          to: event.data.to,
          messageId: streamingMessageId.current,
          textLength: visibleTextLengthRef.current,
        };
      }

      // The target ended without text (a widget-only answer, or a failure): show it now.
      if (
        (event.type === 'data-agentComplete' || event.type === 'data-agentError') &&
        pendingHandoffRef.current?.to === event.data.agentId
      ) {
        flushPendingHandoff();
      }

      if (event.type === 'data-mcpToolsDropped') {
        const { serverNames, toolNames, limit } = event.data;
        const chatId = deps.chats.getChatStoreState().chatId ?? '';
        const dedupeKey = `${chatId}:${serverNames.join(',')}:${toolNames.join(',')}`;
        if (!notifiedMcpToolDropsRef.current.has(dedupeKey)) {
          notifiedMcpToolDropsRef.current.add(dedupeKey);
          notificationService.showWarning(
            t('ai.useAiChat.someToolsWerenTLoaded', {
              limit,
              toolCount: toolNames.length,
              serverNames: serverNames.join(', '),
            }),
          );
        }
      }
    },
    onError: (error) => {
      flushPendingHandoff();
      logger.error('Chat stream error:', error);
      // Check if this is an abort-related error (stop() was called)
      const isAbortError =
        error.name === 'AbortError' ||
        (error instanceof TypeError && error.message.includes('network error')) ||
        (error instanceof Error && error.message.includes('aborted'));

      const wasResuming = isResumingTurn.current;
      isResumingTurn.current = false;

      if (isAbortError) {
        if (!wasResuming) handleAbort(setAISdkMessages);
        clearStreamEvents();
        return;
      }

      if (AgentUnavailableError.is(error)) {
        if (!wasResuming) {
          const lastPrompt = messageInFlight.current?.prompt ?? '';
          // Drop the optimistically-appended user bubble so the refused message
          // doesn't stick around in the thread.
          setAISdkMessages((prev) => prev.slice(0, -1));
          // Restore the user's draft so they can copy it or try again with
          // another agent.
          deps.chats.getChatStoreState().setInputValue(lastPrompt);
        }
        notificationService.showError(t('ai.useAiChat.thisAgentIsNoLonger'), {
          persist: false,
        });
        clearStreamEvents();
        messageInFlight.current = null;
        return;
      }

      // Only true runtime failures — abort and graceful "agent unavailable"
      // have already returned above. Tracking earlier inflated the failure
      // metric with handled UX states.
      const cs = deps.chats.getChatStoreState();
      if (cs.currentAgentId) {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        deps.track('agent_run_failed', {
          agent_id: cs.currentAgentId,
          session_id: cs.chatId ?? undefined,
          error_type: errorObj.name,
          error_code:
            'code' in errorObj ? String((errorObj as { code?: unknown }).code) : undefined,
        });
      }

      notificationService.showError(t('ai.useAiChat.anErrorOccurredWhileProcessing'), {
        persist: false,
        autoHideDuration: 3000,
      });
      messageInFlight.current = null;
    },
    // No `sendAutomaticallyWhen`: the server resolves tool calls, so there is nothing for the
    // client to hand back. Setting it to `lastAssistantMessageIsCompleteWithToolCalls` resubmits
    // a turn that already finished, and the agent answers twice.
  });

  const sendMessage = async (data: SendMessageInputLike) => {
    messageInFlight.current = data;
    isResumingTurn.current = false;
    hasSentMessageRef.current = true;
    clearStreamEvents();

    // Reset suggestions state when sending a new message
    deps.chats.setChatStoreState({ suggestions: [], isSuggestionsLoading: false });

    // If it's a new chat, generate a title in the background
    if (chatHistory?.messages.length === 0 && messages.length === 0) {
      let titlePrompt = data.prompt;
      if (!titlePrompt.trim() && data.files?.length) {
        const fileNames = data.files.map((f) => f.metadata.fileName).join(', ');
        titlePrompt = `[Attached files: ${fileNames}]`;
      }
      void deps.chats
        .generateChatTitle({
          contextId,
          prompt: titlePrompt,
        })
        .catch((error: unknown) => {
          logger.error('Failed to generate chat title:', error);
        });
    }

    // Capture UI context at send time - this ensures we get the current widget state
    const uiContext = deps.app.getUIContextSnapshot();
    const clientLayout = measureChatLayout();

    // Project-context attachment rides through the input options bag. Lift it
    // out so it lands top-level in the body (beside knowledgeBaseIds, matching
    // the wire DTO) instead of inside the agent's input payload.
    const { projectContext: rawProjectContext, ...composerInputs } = data.inputs ?? {};
    const restInputs = data.imageAtDefaults
      ? atImageDefaults(composerInputs, deps.chats.getChatStoreState().agent)
      : composerInputs;
    const projectContext = extractProjectContext(rawProjectContext);
    // Freeze the same attachment onto the message so its chips render
    // immediately, before the server-persisted snapshot is reloaded.
    const projectContextSnapshot = toProjectContextSnapshot(projectContext);

    setInputFiles([]);
    setInputOptions((prev: Record<string, unknown> | undefined) => {
      // Clear the per-message attachments after send — both the image mask and
      // the attached project context — so they don't linger in the composer
      // (the snapshot was already frozen onto the message above).
      const next = deps.chats.dropMaskFromOptions(prev ?? {});
      if (!('projectContext' in next)) return next;
      const cleaned = { ...next };
      delete cleaned.projectContext;
      return cleaned;
    });
    await aiSendMessage(
      {
        text: data.prompt,
        metadata: {
          files: data.files,
          isHidden: data.isHidden,
          projectContext: projectContextSnapshot,
          ...(data.widgetAction ? { widgetAction: widgetActionMarker(data.widgetAction) } : {}),
        },
      },
      {
        body: {
          input: {
            prompt: data.prompt,
            /* @todo: future refactor how mask processing is handled in a way where we can
            have custom pre-processing logic before sending the message to the reasoner */
            ...(await deps.chats.processInputsWithMask(restInputs)),
          },
          knowledgeBaseIds: data.knowledgeBaseIds,
          projectContext,
          files: data.files?.map<FileAsset>((file) => ({
            assetId: file.assetId,
            mimeType: file.mimeType,
            metadata: file.metadata,
          })),
          agentId: data.agentId,
          metadata: {
            isHidden: data.isHidden,
          },
          uiContext,
          ...(clientLayout ? { clientLayout } : {}),
          ...(data.widgetAction ? { widgetAction: data.widgetAction } : {}),
        },
      },
    );
  };

  const convertedMessages = useMemo(() => {
    return chatHistory?.messages.concat(convertAISdkMessagesToChatMessages(messages)) ?? [];
  }, [messages, chatHistory]);

  useEffect(() => {
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    streamingMessageId.current = last.id;
    visibleTextLengthRef.current =
      last.role === 'assistant'
        ? last.parts.reduce((n, part) => (part.type === 'text' ? n + part.text.length : n), 0)
        : 0;

    const pending = pendingHandoffRef.current;
    if (!pending) return;
    const grew =
      pending.messageId === last.id
        ? visibleTextLengthRef.current > pending.textLength
        : visibleTextLengthRef.current > 0;
    if (grew) flushPendingHandoff();
  }, [messages, flushPendingHandoff]);

  // Sync SDK status to store - this is the single source of truth for AI state
  useEffect(() => {
    setAiStatus(status);
  }, [status, setAiStatus]);

  /**
   * Resumes a turn paused on tool approvals with how each was answered. No user message goes with
   * it: the turn is the paused one, and its reply streams into the conversation like any other.
   */
  const answerApprovals = async (
    approvals: readonly { approvalId: string; approved: boolean; answers?: unknown }[],
  ) => {
    messageInFlight.current = null;
    isResumingTurn.current = true;
    clearStreamEvents();
    // The agent the paused turn was sent to, which the server resumes the turn through.
    const agentId = deps.chats.getChatStoreState().currentAgentId;
    await aiSendMessage(undefined, { body: { agentId, approvals, input: {} } });
  };

  return {
    messages: convertedMessages,
    isNewChat: chatHistory?.messages.length === 0,
    isLoading,
    status,
    sendMessage,
    answerApprovals,
    /** Stops this conversation's stream only; another chat on the page keeps running. */
    stop,
  };
};

/**
 * Image settings still at the agent's defaults are left out of a request, so each Generate image
 * node, a sub-agent's included, runs on its own; what the chip shows goes beside them, so the agent
 * asks before using anything else. Read off the card as it is when the request leaves, which a
 * builder's test chat reloads just before.
 */
function atImageDefaults(
  inputs: Record<string, unknown>,
  agent: ReturnType<UseAiChatDeps['chats']['getChatStoreState']>['agent'],
): Record<string, unknown> {
  const shown = agent?.capabilities?.imageGeneration?.defaults ?? {
    settings: inputs.settings,
    variations: inputs.variations,
  };
  return {
    ...omit(inputs, ['settings', 'variations', 'mask']),
    imageShown: { settings: shown.settings, variations: shown.variations },
  };
}
